import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Layout } from "@/components/Layout";
import { SearchBar } from "@/components/SearchBar";
import { CATEGORIES, LAATSTE_IMPORT } from "@/data/products";
import { CtaBlock } from "@/components/CtaBlock";
import { RvoNotice } from "@/components/RvoNotice";
import { Database, FileCheck2, Sparkles } from "lucide-react";
import { Seo, faqLd, SITE_URL } from "@/lib/seo";
import { supabase } from "@/integrations/supabase/client";

const FAQ = [
  { question: "Wat is een ISDE meldcode?", answer: "Een ISDE-meldcode is een unieke code die RVO toekent aan apparaten en materialen die in aanmerking komen voor de Investeringssubsidie Duurzame Energie en Energiebesparing." },
  { question: "Waar vind ik de officiële ISDE-meldcodelijst?", answer: "De officiële meldcodelijsten worden gepubliceerd door RVO op rvo.nl. Wij importeren deze lijsten periodiek in onze zoekmachine." },
  { question: "Is deze website van de RVO?", answer: "Nee. ISDE Meldcode Zoeker is een onafhankelijk informatief platform en geen onderdeel van RVO of de overheid." },
  { question: "Hoe vaak wordt de database bijgewerkt?", answer: "Wij verwerken nieuwe RVO-lijsten zodra ze worden gepubliceerd, doorgaans één keer per kwartaal." },
];

const DEFINED_TERMS = [
  { name: "ISDE", description: "Investeringssubsidie Duurzame Energie en Energiebesparing — een Nederlandse subsidieregeling van RVO." },
  { name: "Meldcode", description: "Unieke code die RVO toekent aan een product of materiaal dat in aanmerking komt voor ISDE-subsidie." },
  { name: "Subsidiebedrag", description: "Het indicatieve bedrag dat RVO uitkeert voor een product met geldige ISDE-meldcode, op basis van de meldcodelijst." },
];

const Index = () => {
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [lastImport, setLastImport] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const totalPromise = supabase
        .from("products").select("*", { count: "exact", head: true }).eq("is_active", true);
      const categoryPromises = CATEGORIES.map((c) =>
        supabase
          .from("products").select("*", { count: "exact", head: true })
          .eq("category", c.slug).eq("is_active", true)
      );
      const sourcePromise = supabase
        .from("source_files").select("downloaded_at")
        .eq("status", "completed").order("downloaded_at", { ascending: false }).limit(1).maybeSingle();

      const [totalRes, sourceRes, ...categoryResults] = await Promise.all([
        totalPromise,
        sourcePromise,
        ...categoryPromises,
      ]);

      setTotalCount(totalRes.count ?? 0);
      const next: Record<string, number> = {};
      CATEGORIES.forEach((c, i) => {
        next[c.slug] = categoryResults[i].count ?? 0;
      });
      setCounts(next);
      if (sourceRes.data?.downloaded_at) {
        setLastImport(new Date(sourceRes.data.downloaded_at).toISOString().slice(0, 10));
      }
    })();
  }, []);
  const websiteLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "ISDE Meldcode Zoeker",
    url: SITE_URL,
    inLanguage: "nl-NL",
    potentialAction: {
      "@type": "SearchAction",
      target: `${SITE_URL}/zoeken?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };
  const orgLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "ISDE Meldcode Zoeker",
    url: SITE_URL,
    description: "Onafhankelijk informatief platform voor ISDE-meldcodes op basis van officiële RVO-lijsten.",
  };
  const catalogLd = {
    "@context": "https://schema.org",
    "@type": "DataCatalog",
    name: "ISDE meldcodelijsten",
    url: `${SITE_URL}/datakwaliteit`,
    dataset: CATEGORIES.map((c) => ({
      "@type": "Dataset",
      name: `RVO meldcodelijst ${c.naam}`,
      description: `Officiële RVO-meldcodelijst voor ${c.naam.toLowerCase()}, geïmporteerd door ISDE Meldcode Zoeker.`,
      url: `${SITE_URL}/categorie/${c.slug}`,
      isAccessibleForFree: true,
      creator: { "@type": "Organization", name: "Rijksdienst voor Ondernemend Nederland (RVO)", url: "https://www.rvo.nl/" },
      license: "https://www.rvo.nl/",
      keywords: ["ISDE", "meldcode", c.naam],
    })),
  };
  const definedTermsLd = DEFINED_TERMS.map((t) => ({
    "@context": "https://schema.org",
    "@type": "DefinedTerm",
    name: t.name,
    description: t.description,
    inDefinedTermSet: { "@type": "DefinedTermSet", name: "ISDE woordenlijst", url: SITE_URL },
  }));
  return (
    <Layout>
      <Seo
        title="ISDE Meldcode Zoeker — vind je meldcode en subsidiebedrag"
        description="Zoek snel ISDE-meldcodes, merken, producten en subsidiebedragen op basis van de laatst geïmporteerde officiële RVO-meldcodelijsten."
        path="/"
        jsonLd={[websiteLd, orgLd, catalogLd, faqLd(FAQ), ...definedTermsLd]}
      />
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-soft" />
        <div className="container relative grid gap-10 py-16 md:py-24">
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground shadow-card">
              <Sparkles className="h-3.5 w-3.5 text-primary" />
              Gebaseerd op officiële RVO-meldcodelijsten
            </span>
            <h1 className="mt-5 text-3xl font-semibold tracking-tight break-words sm:text-4xl md:text-6xl">
              Zoek snel je <span className="text-primary">ISDE meldcode</span>
            </h1>
            <p className="mx-auto mt-4 max-w-2xl text-base text-muted-foreground md:text-lg">
              Vind meldcodes, merken, producten en subsidiebedragen op basis van de laatst
              geïmporteerde RVO-meldcodelijsten.
            </p>
            <div className="mx-auto mt-8 max-w-2xl">
              <SearchBar size="lg" />
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              Bijvoorbeeld: <button className="underline-offset-2 hover:underline" onClick={() => (window.location.href = "/zoeken?q=KA00123")}>KA00123</button>,{" "}
              <button className="underline-offset-2 hover:underline" onClick={() => (window.location.href = "/zoeken?q=Daikin")}>Daikin</button>,{" "}
              <button className="underline-offset-2 hover:underline" onClick={() => (window.location.href = "/zoeken?q=warmtepomp")}>warmtepomp</button>
            </p>
          </div>

          {/* Stats */}
          <div className="mx-auto grid w-full max-w-4xl gap-4 sm:grid-cols-3">
            <div className="rounded-xl border border-border bg-card p-5 shadow-card">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Database className="h-4 w-4 text-primary" /> Producten in database
              </div>
              <p className="mt-2 text-2xl font-semibold">{totalCount ?? "…"}</p>
            </div>
            <div className="rounded-xl border border-border bg-card p-5 shadow-card">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <FileCheck2 className="h-4 w-4 text-primary" /> Laatste import
              </div>
              <p className="mt-2 text-2xl font-semibold">{lastImport ?? LAATSTE_IMPORT}</p>
            </div>
            <div className="rounded-xl border border-border bg-card p-5 shadow-card">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Sparkles className="h-4 w-4 text-primary" /> Categorieën
              </div>
              <p className="mt-2 text-2xl font-semibold">{CATEGORIES.length}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Categorieën */}
      <section className="container py-12">
        <div className="mb-6 flex items-end justify-between">
          <h2 className="text-2xl font-semibold">Bladeren per categorie</h2>
          <Link to="/zoeken" className="text-sm text-primary hover:underline">
            Alle producten →
          </Link>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {CATEGORIES.map((c) => {
            const count = counts[c.slug] ?? 0;
            return (
              <Link
                key={c.slug}
                to={`/categorie/${c.slug}`}
                className="group rounded-xl border border-border bg-card p-6 shadow-card transition-smooth hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-elegant"
              >
                <p className="text-xs uppercase tracking-wider text-muted-foreground">
                  {count} producten
                </p>
                <h3 className="mt-1 text-lg font-semibold group-hover:text-primary">{c.naam}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{c.omschrijving}</p>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Uitleg + CTA */}
      <section className="container grid gap-6 py-12 md:grid-cols-3">
        <div className="rounded-2xl border border-border bg-card p-6 shadow-card md:col-span-2">
          <h2 className="text-xl font-semibold">Wat is een ISDE meldcode?</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            De ISDE-meldcode is een unieke code die de Rijksdienst voor Ondernemend Nederland (RVO)
            toekent aan apparaten en materialen die in aanmerking komen voor de
            Investeringssubsidie Duurzame Energie en Energiebesparing. Alleen producten met een
            geldige meldcode op de officiële RVO-lijst komen in aanmerking voor subsidie.
          </p>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Met deze zoekmachine controleer je in één oogopslag of een product op de laatst
            geïmporteerde lijst staat, in welke categorie het valt en welk subsidiebedrag erbij
            hoort.
          </p>
          <div className="mt-5">
            <RvoNotice />
          </div>
        </div>
        <CtaBlock placement="search_results_top" fallback={{ title: "Vergelijk vrijblijvend offertes", description: "Ontvang offertes van geverifieerde installateurs in jouw regio." }} />
      </section>
    </Layout>
  );
};

export default Index;
